import React, { useEffect, useRef, useState } from 'react';

interface Props {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  /** reveal direction: up (default), left, right, scale */
  variant?: 'up' | 'left' | 'right' | 'scale';
  /** extra props passthrough (onClick, title, data-*) */
  [key: string]: any;
}

/**
 * Reproduces the original IntersectionObserver fade-in: elements start with
 * `.fade-in` and gain `.visible` when they enter the viewport (threshold .1).
 */
const FadeIn: React.FC<Props> = ({ className = '', style, children, variant = 'up', ...rest }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const variantClass = variant !== 'up' ? ` fade-${variant}` : '';

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`fade-in${variantClass}${visible ? ' visible' : ''}${className ? ' ' + className : ''}`}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
};

export default FadeIn;
